import { useState } from 'react';
import { Circle, GitBranch, Users } from 'lucide-react';
import DateSelect from '../components/DateSelect.jsx';
import LiveIndicator from '../components/LiveIndicator.jsx';
import UpdatedAt from '../components/UpdatedAt.jsx';
import { formatMinutes } from '../lib/format.js';
import { today } from '../lib/dateRange.js';
import { useLiveData } from '../lib/useLiveData.js';
import { useLiveStatus } from '../lib/useLiveStatus.js';

/* Queues & ring groups — tenant admin only (the backend enforces it).

   Two time frames on this page, and they mean different things:

   - "Right now" — who's waiting, which agents are available, the live roster.
     Only meaningful for today; a queue has no "agents available yesterday".
   - Performance for a chosen day — calls received, answered and missed, from
     the CDR. This is what the date selector drives.

   Both are discovered by type from VitalPBX, so any queue/ring-group codes are
   picked up automatically. Ring groups expose far less than queues (no live
   state, no per-day figures), and the card says so rather than faking it. */
export default function Queues() {
  const [date, setDate] = useState(today);
  const isToday = date === today();

  const live = useLiveData('/queues', { intervalMs: 15000 });
  // The queue counts (waiting, agents available) still come from the poll above
  // — they're not per-extension device states. The agent roster dots are the
  // live part: pushed over the same SSE stream the Extensions page uses, so an
  // agent going on a call turns amber the instant it happens.
  const liveStatus = useLiveStatus();
  // Today's figures accumulate through the day; a past day is settled, so it
  // needs no refresh once loaded.
  const day = useLiveData(`/queue-stats?date=${date}`, { intervalMs: isToday ? 30000 : 3600000 });

  if (live.status === 'loading') {
    return <div className="card"><div className="empty"><p className="empty__body">Loading your queues…</p></div></div>;
  }

  if (live.status === 'error') {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">We couldn't load your queues</div>
          <p className="empty__body">{live.error?.message}</p>
        </div>
      </div>
    );
  }

  const groups = live.data?.items ?? [];
  const dayStats = day.data?.stats ?? {};
  /* The day's figures are either still in flight, or they have arrived. A
     queue with no entry in a payload that HAS arrived took no calls that day —
     which is a fact worth stating, not a reason to keep saying "loading". */
  const dayLoaded = day.status === 'ready';
  const dayFailed = day.status === 'error' && !day.data;

  if (groups.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">No queues or ring groups</div>
          <p className="empty__body">Your account doesn't have any call queues or ring groups set up.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <DateSelect value={date} onChange={setDate} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Live dots only mean "now", so the cue is only honest for today. */}
          {isToday && <LiveIndicator connected={liveStatus.connected} supported={liveStatus.supported} />}
          <UpdatedAt at={live.updatedAt} onRefresh={live.refresh} stale={Boolean(live.error)} />
        </div>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {groups.map((g) => (
          g.type === 'ring_group'
            ? <RingGroupCard key={`rg-${g.extension}`} group={g} liveStatus={liveStatus.byExtension} />
            : (
              <QueueCard
                key={`q-${g.extension}`}
                queue={g}
                dayStats={dayLoaded ? (dayStats[g.extension] ?? NO_CALLS) : undefined}
                dayFailed={dayFailed}
                isToday={isToday}
                liveStatus={liveStatus.byExtension}
              />
            )
        ))}
      </div>
    </div>
  );
}

/* A ring group rings a set of extensions at once — no queueing, no agents in
   the queue sense, so no live wait/agent tiles. Its members come from the
   dialplan (via AMI), each with live status, so the roster renders the same
   way a queue's does. */
function RingGroupCard({ group, liveStatus = {} }) {
  const members = group.members ?? [];
  const known = group.member_count != null;

  return (
    <div className="card">
      <div className="card__head">
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GitBranch size={17} color="var(--signal-deep)" />
          {group.name}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink-soft)', fontWeight: 400 }}>
            ext {group.extension}
          </span>
        </span>
        <span className="pill pill--offline">Ring group</span>
      </div>

      <div style={{ padding: '14px 20px 4px', fontSize: 13, color: 'var(--ink-soft)' }}>
        Rings all its members together
        {group.ring_timeout ? ` for ${group.ring_timeout}s` : ''}.
      </div>

      {!known && (
        <div style={{ padding: '4px 20px 16px', fontSize: 13, color: 'var(--ink-soft)' }}>
          We couldn't read the member list right now.
        </div>
      )}

      {known && members.length > 0 && (
        <>
          <div style={{ padding: '8px 20px 4px', fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-soft)' }}>
            {members.length} member{members.length === 1 ? '' : 's'}
          </div>
          <div style={{ padding: '4px 12px 12px' }}>
            {members.map((m) => <AgentRow key={m.extension} member={m} liveState={liveStatus[m.extension]?.state} />)}
          </div>
        </>
      )}
    </div>
  );
}

/* A queue that took no calls on the chosen day.

   The stats endpoint returns only queues that saw traffic, so a quiet queue is
   simply absent from the payload. Standing in a zeroed record here is what
   turns that absence into "0 calls received" rather than a spinner that never
   resolves.

   answer_rate is null rather than 0: answering none of no calls is not a 0%
   performance, and the tile hides itself when the rate is null. */
const NO_CALLS = {
  received: 0,
  answered: 0,
  missed: 0,
  voicemail: 0,
  talk_seconds: 0,
  answer_rate: null,
  by_agent: {},
};

function QueueCard({ queue, dayStats, dayFailed = false, isToday, liveStatus = {} }) {
  const live = queue.live;

  return (
    <div className="card">
      <div className="card__head">
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Users size={17} color="var(--signal-deep)" />
          {queue.name}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink-soft)', fontWeight: 400 }}>
            ext {queue.extension}
          </span>
        </span>
        <span className="pill pill--offline">{queue.strategy}</span>
      </div>

      {/* Performance for the selected day, from the CDR. */}
      <div style={{ padding: '8px 20px 2px', fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-soft)' }}>
        {isToday ? 'Today' : 'On this day'}
      </div>
      <div style={{ display: 'flex', gap: 28, padding: '6px 20px 16px', flexWrap: 'wrap' }}>
        {dayStats ? (
          <>
            <Tile label="Calls received" value={dayStats.received} />
            <Tile label="Answered" value={dayStats.answered} tone="var(--mint)" />
            <Tile
              label="Missed"
              value={dayStats.missed}
              tone={dayStats.missed > 0 ? 'var(--miss)' : undefined}
            />
            {dayStats.answer_rate != null && <Tile label="Answer rate" value={`${dayStats.answer_rate}%`} />}
            <Tile label="Talk time" value={formatMinutes(dayStats.talk_seconds)} />
          </>
        ) : (
          <p className="empty__body" style={{ margin: 0 }}>
            {dayFailed
              ? "We couldn't load this day's figures."
              : "Loading the day's figures…"}
          </p>
        )}
      </div>

      {/* Live "right now" strip — only meaningful for today. */}
      {isToday && live && (
        <div style={{ display: 'flex', gap: 28, padding: '4px 20px 16px', flexWrap: 'wrap', borderTop: '1px solid var(--line)' }}>
          <div style={{ width: '100%', paddingTop: 12, fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-soft)' }}>
            Right now
          </div>
          <Tile
            label="Waiting"
            value={live.callers_waiting}
            tone={live.callers_waiting > 0 ? 'var(--amber)' : undefined}
          />
          <Tile
            label="Agents available"
            // Available now out of the total agents in the queue — not out of
            // how many happen to be logged in.
            value={`${live.agents_available} of ${queue.member_count}`}
            tone={live.agents_available === 0 ? 'var(--miss)' : 'var(--mint)'}
          />
          <Tile label="Longest wait" value={formatWait(live.longest_wait_seconds)} />
          {live.service_level_pct != null && (
            <Tile
              label={`Service level (${queue.service_level_seconds}s)`}
              value={`${Math.round(live.service_level_pct)}%`}
            />
          )}
        </div>
      )}

      {/* Agent roster. Live status dots are current, so they only carry meaning
          for today; on a past day the roster is just who's in the queue. */}
      <div style={{ borderTop: '1px solid var(--line)' }}>
        <div style={{ padding: '12px 20px 4px', fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-soft)' }}>
          {queue.member_count} agent{queue.member_count === 1 ? '' : 's'}
        </div>
        <div style={{ padding: '4px 12px 12px' }}>
          {queue.members.map((m) => (
            <AgentRow
              key={m.extension}
              member={m}
              showState={isToday}
              // Live device state pushed over SSE; only meaningful for today,
              // so it's ignored when showState is off (a past day).
              liveState={liveStatus[m.extension]?.state}
              // Calls this agent answered in the queue for the selected day —
              // not AMI's lifetime total. null while the day's figures load.
              taken={dayStats ? (dayStats.by_agent?.[m.extension] ?? 0) : null}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AgentRow({ member, showState = true, taken = null, liveState = null }) {
  // Pushed device state wins over the value that rode in with the queue poll —
  // that's what makes the dot turn amber the instant an agent picks up. Pause
  // is a queue concept AMI's device state doesn't carry, so it stays on top.
  // On a past day (showState off) the live "now" would mislead, so ignore it.
  const effectiveState = member.paused
    ? 'paused'
    : ((showState && liveState) ? liveState : member.state);
  const s = AGENT_STATE[effectiveState] ?? AGENT_STATE.unknown;
  const label = member.paused ? 'Paused' : s.label;
  // Live status is "now", so on a past day the dot would be misleading — show
  // the roster without it.
  const tone = !showState ? 'var(--muted)' : s.tone;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px' }}>
      <Circle size={9} fill={tone} color={tone} />
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--signal-deep)', width: 44 }}>
        {member.extension}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 500 }}>{member.name ?? `Extension ${member.extension}`}</span>
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
        {taken != null && (
          <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
            {taken} taken
          </span>
        )}
        {showState && (
          <span style={{ fontSize: 12.5, color: tone, width: 78, textAlign: 'right' }}>{label}</span>
        )}
      </span>
    </div>
  );
}

const AGENT_STATE = {
  available: { label: 'Available', tone: 'var(--mint)' },
  // Queue members report "available", device state reports "idle" — same
  // thing (registered, not on a call), so they read identically.
  idle: { label: 'Available', tone: 'var(--mint)' },
  on_call: { label: 'On a call', tone: 'var(--amber)' },
  busy: { label: 'Busy', tone: 'var(--amber)' },
  ringing: { label: 'Ringing', tone: 'var(--amber)' },
  on_hold: { label: 'On hold', tone: 'var(--amber)' },
  paused: { label: 'Paused', tone: 'var(--amber)' },
  unreachable: { label: 'Not responding', tone: 'var(--amber)' },
  offline: { label: 'Offline', tone: 'var(--muted)' },
  unknown: { label: '—', tone: 'var(--muted)' },
};

function Tile({ label, value, tone }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 24, letterSpacing: '-0.02em', color: tone }}>
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>{label}</div>
    </div>
  );
}

/** Waits are seconds-granular: 0 → "None", 45 → "45s", 90 → "1m 30s". */
function formatWait(seconds) {
  if (!seconds || seconds <= 0) return 'None';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}
